// src/pages/cmdb/ViewScriptModal.tsx
// Q-927 Sprint 6 — Modal viewer del contenido SQL cacheado de un artifact.
// Reusa el endpoint /api/cmdb/scripts/{sistema}/{artifact_id}/content de Q-927.
import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
  Button, Spinner, toast, Badge,
} from '@/components/ui';
import { Copy, Download, FileCode2 } from 'lucide-react';
import { cmdbService } from '@/services/cmdbService';
import type { DeployPlanArtifact, ScriptContentResponse } from '@/services/cmdbService';

interface Props {
  sistema: string;
  artifact: DeployPlanArtifact;
  onClose: () => void;
}

export function ViewScriptModal({ sistema, artifact, onClose }: Props) {
  const [content, setContent] = useState<ScriptContentResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    cmdbService.getScriptContent(sistema, artifact.artifact_id)
      .then((r) => {
        if (!cancelled) setContent(r);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : String(e);
        setError(msg);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [sistema, artifact.artifact_id]);

  const handleCopy = async () => {
    if (!content) return;
    try {
      await navigator.clipboard.writeText(content.content_text);
      toast.success('SQL copiado al portapapeles');
    } catch {
      toast.error('No se pudo copiar al portapapeles');
    }
  };

  const handleDownload = () => {
    if (!content) return;
    const filename = (content.path || `${artifact.artifact_id}.sql`).split('/').pop() ?? `${artifact.artifact_id}.sql`;
    const blob = new Blob([content.content_text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileCode2 className="h-5 w-5" />
            {artifact.artifact_id}
          </DialogTitle>
          <div className="text-xs text-muted-foreground space-y-1">
            <div className="font-mono">{artifact.path || '(sin path)'}</div>
            <div className="flex gap-2 flex-wrap">
              <Badge variant="outline">{artifact.type}</Badge>
              <Badge variant="outline">{artifact.scope}</Badge>
              {content && (
                <>
                  <Badge variant="outline">{content.content_size_bytes} bytes</Badge>
                  <Badge variant="outline">sha256: {content.sha256_actual.slice(0, 12)}…</Badge>
                </>
              )}
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-auto border rounded bg-zinc-950 dark:bg-zinc-900">
          {loading && (
            <div className="flex justify-center items-center h-32">
              <Spinner />
            </div>
          )}
          {error && (
            <div className="p-4 text-red-400 text-sm" data-testid="view-script-error">
              <strong>Error:</strong> {error}
            </div>
          )}
          {content && (
            <pre
              className="p-4 text-xs font-mono text-zinc-100 whitespace-pre overflow-x-auto"
              data-testid="view-script-content"
            >
              {content.content_text || '(contenido vacío)'}
            </pre>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleDownload}
            disabled={!content}
            data-testid="view-script-download-btn"
          >
            <Download className="h-4 w-4 mr-1" /> Descargar
          </Button>
          <Button
            onClick={handleCopy}
            disabled={!content}
            data-testid="view-script-copy-btn"
          >
            <Copy className="h-4 w-4 mr-1" /> Copiar
          </Button>
          <Button variant="ghost" onClick={onClose}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
